import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCartPlus, faTag } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import api from '../../config/api';
import parameterService from '../../services/parameterService';
import './ProductCard.css';
import toastService from '../../services/toastService';
import { Modal, Button } from 'react-bootstrap';

const CustomDesignCard = ({ phoneModels, onCustomDesignClick }) => {
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const [showColorInfo, setShowColorInfo] = useState(false);
  const [kendinTasarlaFee, setKendinTasarlaFee] = useState(null);

  // Kendin tasarla ücretini yükle
  useEffect(() => {
    const loadKendinTasarlaFee = async () => {
      try {
        // Cache'i temizle ve yeniden yükle
        parameterService.clearCache();
        const fee = await parameterService.getKendinTasarlaFee();
        console.log('Yüklenen kendin tasarla ücreti:', fee);
        if (fee !== null) {
          setKendinTasarlaFee(fee);
        }
      } catch (error) {
        console.error('Kendin tasarla ücreti yüklenirken hata:', error);
      }
    };

    loadKendinTasarlaFee();
  }, []);

  // Telefon modeli değiştiğinde renk seçeneklerini güncelle
  useEffect(() => {
    const fetchColorOptions = async () => {
      if (!selectedModel) {
        setAvailableColors([]);
        setSelectedColor(null);
        return;
      }

      try {
        // Renk seçeneklerinin yüklendiğini belirt
        setLoadingColors(true);
        
        // Seçilen telefon modeli için renk seçeneklerini getir (kendin tasarla için sublimasyon = 1)
        const response = await api.get(`/phone-models/${selectedModel}/colors?product_type_id=1`);
        if (response.data) {
          setAvailableColors(response.data);
          setSelectedColor(null); // Renk seçimini sıfırla
        }
      } catch (err) {
        console.error('Error fetching color options:', err);
      } finally {
        // Yükleme işlemi tamamlandı
        setLoadingColors(false);
      }
    };

    if (selectedModel) {
      fetchColorOptions();
    }
  }, [selectedModel]);

  const handleColorSelect = (e, color) => {
    e.stopPropagation();
    setSelectedColor(color);
  };

  const handleCustomDesignClick = () => {
    // Eğer kullanıcı herhangi bir seçim yapmamışsa direkt olarak yönlendir
    if (!selectedModel && availableColors.length === 0) {
      onCustomDesignClick(null, null);
      return;
    }

    // Renk seçenekleri yükleniyorsa işlemi engelle
    if (loadingColors) {
      toastService.showColorsLoadingToast();
      return;
    }

    // Telefon modeli seçilmiş ama renk seçilmemişse
    if (selectedModel && availableColors.length > 0 && !selectedColor) {
      toastService.showColorNotSelectedToast();
      return;
    }

    // Seçilen modelin tam bilgilerini bul
    const selectedPhoneModel = phoneModels.find(model => model.id === parseInt(selectedModel));
    
    if (selectedModel && !selectedPhoneModel) {
      toastService.showModelNotFoundToast();
      return;
    }

    // Kendin Tasarla sayfasına yönlendir
    onCustomDesignClick(selectedPhoneModel, selectedColor);
  };

  const handleColorInfoClick = (e) => {
    e.stopPropagation();
    setShowColorInfo(true);
  };

  const handleCloseColorInfo = () => {
    setShowColorInfo(false);
  };

  // Active phone models list
  const activePhoneModels = phoneModels?.filter(model => model?.is_active) || [];

  return (
    <>
      <div className="product-card" onClick={handleCustomDesignClick}>
        <div className="image-container">
          <div className="custom-design-overlay">
            <div className="custom-design-content">
              <div className="sparkle-effects">
                <div className="sparkle"></div>
                <div className="sparkle"></div>
                <div className="sparkle"></div>
              </div>
              <div className="design-main-icon">
                <img src="/images/icons/star-wand.png" alt="Magic Wand" className="wand-icon" />
              </div>
              <h3 className="custom-design-title">Kendi Tasarımın</h3>
              <p className="custom-design-subtitle">Hayal Et, Oluştur, Şaşırt</p>
            </div>
          </div>
        </div>
        <div className="card-body">
          <h5 className="product-title">Kendi Tasarımını Oluştur</h5>
          
          {activePhoneModels && activePhoneModels.length > 0 && (
            <div className="models-container">
              <span className="models-label">Uyumlu Modeller:</span>
              <select 
                className="model-select"
                value={selectedModel}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedModel(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="">Model seçin</option>
                {activePhoneModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.brand || ''} {model.model_name || model.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedModel && (loadingColors ? (
            <div className="colors-container loading">
              <div className="colors-label-container">
                <span className="colors-label">Renk Seçenekleri Yükleniyor...</span>
              </div>
              <div className="color-loading">
                <div className="color-loading-indicator"></div>
              </div>
            </div>
          ) : availableColors.length > 0 && (
            <div className="colors-container">
              <div className="colors-label-container">
                <span className="colors-label">Çerçeve Rengi:</span>
                <FontAwesomeIcon 
                  icon={faInfoCircle} 
                  className="color-info-icon" 
                  onClick={handleColorInfoClick}
                />
              </div>
              <div className="color-options">
                {availableColors.map(color => {
                  const colorCode = color.code;
                  const isWhiteColor = colorCode === '#FFFFFF' || colorCode === 'white' || colorCode === '#FFF';
                  const isTransparentColor = colorCode === 'transparent' || colorCode === 'rgba(0,0,0,0)';
                  const isDarkColor = colorCode === '#000000' || colorCode === 'black' || colorCode === '#000';
                  const isLightColor = isWhiteColor || isTransparentColor;
                  
                  return (
                    <button
                      key={color.id}
                      className={`color-option ${selectedColor?.id === color.id ? 'selected' : ''} ${isTransparentColor ? 'transparent-color' : ''} ${isWhiteColor ? 'white-color' : ''}`}
                      style={{ 
                        backgroundColor: colorCode,
                        border: isLightColor ? '2px solid #ddd' : 'none'
                      }}
                      onClick={(e) => handleColorSelect(e, color)}
                      title={color.name}
                    >
                      {isTransparentColor && <div className="transparent-pattern"></div>}
                      {selectedColor?.id === color.id && (
                        <span className={`color-check ${isLightColor ? 'dark-check' : ''} ${isDarkColor ? 'light-check' : ''}`}>
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedColor && (
                <div className="selected-color-name">
                  <span>{selectedColor.name}</span>
                </div>
              )}
            </div>
          ))}

          <div className="price-container">
            <span className="price">{kendinTasarlaFee !== null ? kendinTasarlaFee.toFixed(2) : 'Yükleniyor...'} TL</span>
          </div>

          <button 
            className="add-to-cart-btn"
            onClick={handleCustomDesignClick}
          >
            
            <span>Kendin Tasarla</span>
          </button>
        </div>
      </div>

      {/* Color Info Modal */}
      <Modal 
        show={showColorInfo} 
        onHide={handleCloseColorInfo} 
        centered
        className="color-info-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Renk Seçenekleri Hakkında</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Değerli Müşterilerimiz,</p>
          <p>Sublimasyon baskı ile üretilen sert plastik telefon kılıflarımız, yalnızca beyaz, siyah ve transparan yan kenar renklerine sahiptir.</p>
          <ul>
            <li><strong>Beyaz Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları beyazdır.</li>
            <li><strong>Siyah Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları siyahtır.</li>
            <li><strong>Transparan Kılıflar:</strong> Arka yüzeyi baskıya uygundur, yan kenarları şeffaftır ve telefonunuzun rengini gösterir.</li>
          </ul>
          <p>Baskı işlemi yalnızca kılıfın arka yüzeyine uygulanmaktadır. Yan kenarlara herhangi bir baskı yapılmamaktadır. Sipariş verirken bu bilgiyi göz önünde bulundurmanızı rica ederiz.</p>
          <p>Anlayışınız için teşekkür eder, keyifli alışverişler dileriz!</p>
          <p><strong>Mr Brown Case</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseColorInfo}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomDesignCard; 